import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Check, ChevronDown, ChevronRight } from 'lucide-react';
import type { PurchaseFlow, FlowStatus, PurchaseStage } from '@/types/purchaseFlow';
import { STAGE_ORDER } from '@/types/purchaseFlow';

interface FlowListPanelProps {
  className?: string;
  flows: PurchaseFlow[];
  activeFlowId: string | null;
  onSelectFlow: (flowId: string) => void;
  onCreateNew?: () => void;
}

const STATUS_DOT: Record<FlowStatus, string> = {
  active:    'bg-green-500',
  paused:    'bg-amber-500',
  completed: 'bg-gray-400',
  cancelled: 'bg-red-400',
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getStageState(stage: PurchaseStage, current: PurchaseStage): 'completed' | 'current' | 'upcoming' {
  const stageIdx = STAGE_ORDER.indexOf(stage);
  const currentIdx = STAGE_ORDER.indexOf(current);
  if (stageIdx < currentIdx) return 'completed';
  if (stageIdx === currentIdx) return 'current';
  return 'upcoming';
}

const FlowListPanel: React.FC<FlowListPanelProps> = ({ className, flows, activeFlowId, onSelectFlow, onCreateNew }) => {
  const { t } = useTranslation('purchaseflow');
  const [expandedFlowId, setExpandedFlowId] = useState<string | null>(activeFlowId);

  const toggleExpand = (flowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedFlowId(prev => prev === flowId ? null : flowId);
  };

  return (
    <div className={`flex-col h-full overflow-hidden border-r border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {t('flowList.title')}
        </span>
        <button
          onClick={onCreateNew}
          className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Flow list */}
      <div className="flex-1 overflow-y-auto">
        {flows.length === 0 && (
          <div className="px-3 py-4 text-xs text-gray-400 text-center">
            {t('flowList.noFlows')}
          </div>
        )}
        {flows.map(flow => {
          const isActive = flow.flow_id === activeFlowId;
          const isExpanded = expandedFlowId === flow.flow_id;
          const stage = flow.current_stage as PurchaseStage;
          const vendorName = (flow.stage_data?.vendor as Record<string, string>)?.name || flow.vendor_code || '';

          return (
            <div key={flow.flow_id}>
              {/* Flow card */}
              <button
                onClick={() => onSelectFlow(flow.flow_id)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 dark:border-gray-800 transition-colors ${
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800 border-l-2 border-l-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  {/* Expand toggle */}
                  <span
                    onClick={(e) => toggleExpand(flow.flow_id, e)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 cursor-pointer"
                  >
                    {isExpanded
                      ? <ChevronDown className="w-3 h-3" />
                      : <ChevronRight className="w-3 h-3" />
                    }
                  </span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    isActive && flow.status === 'active' ? 'bg-blue-500 animate-pulse' : STATUS_DOT[flow.status]
                  }`} />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                    {(flow.stage_data as Record<string, unknown>)?.po_number as string || `Flow ${flow.flow_id.slice(0, 8)}`}
                  </span>
                </div>
                {vendorName && (
                  <div className="mt-0.5 ml-7 text-[10px] text-gray-500 dark:text-gray-400 truncate">
                    {vendorName}
                  </div>
                )}
                <div className="mt-0.5 ml-7 flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {t(`stages.${stage}`)}
                  </span>
                  <span className="text-[10px] text-gray-300 dark:text-gray-600">·</span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {relativeTime(flow.updated_at || flow.created_at)}
                  </span>
                </div>
              </button>

              {/* Expandable stage progress tree */}
              {isExpanded && (
                <div className={`pl-5 pr-2 py-2 border-b border-gray-50 dark:border-gray-800 ${
                  isActive ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-gray-50/50 dark:bg-gray-800/30'
                }`}>
                  {STAGE_ORDER.map((s, i) => {
                    const state = getStageState(s, flow.current_stage);
                    return (
                      <div key={s} className="flex items-center gap-2 py-0.5">
                        {/* Connector + dot */}
                        <div className="flex items-center gap-0 w-5 justify-center flex-shrink-0">
                          {i > 0 && (
                            <div className={`absolute -mt-3 w-px h-2 ${
                              getStageState(STAGE_ORDER[i - 1], flow.current_stage) === 'completed'
                                ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-gray-200 dark:bg-gray-700'
                            }`} />
                          )}
                          <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                            state === 'completed' ? 'bg-emerald-500 text-white' :
                            state === 'current' ? 'bg-blue-500 text-white ring-2 ring-blue-200 dark:ring-blue-800' :
                            'bg-gray-200 dark:bg-gray-600'
                          }`}>
                            {state === 'completed'
                              ? <Check className="w-2 h-2" />
                              : <span className="text-[7px] font-medium">{i + 1}</span>
                            }
                          </div>
                        </div>
                        <span className={`text-[10px] truncate ${
                          state === 'current' ? 'font-semibold text-blue-600 dark:text-blue-400' :
                          state === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                          'text-gray-400 dark:text-gray-500'
                        }`}>
                          {t(`stages.${s}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlowListPanel;
